import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, concatMap, mergeMap } from 'rxjs/operators';
import { of } from 'rxjs';
import { GiasAssessmentActions } from './gias-assessment.actions';
import { GiasAssessmentService } from '../../service/gias-assessment.service';
import { MessageService } from 'primeng/api';


@Injectable()
export class GiasAssessmentEffects {

  loadGiasAssessment$ = createEffect(
    (cd: GiasAssessmentService = inject(GiasAssessmentService)) => {
      return this.actions$.pipe(

        ofType(GiasAssessmentActions.loadGiasAssessments),
        mergeMap(({ campaignId, parameters, attestationId, legalEntityIds }) =>
          cd.getGiasAssessment(campaignId, attestationId,legalEntityIds,  parameters ).pipe(
            map((giasAssessments) => {
              return GiasAssessmentActions.loadGiasAssessmentsSuccess({
                giasAssessments
              });
            }),
            catchError((error) => of(GiasAssessmentActions.loadGiasAssessmentsFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  loadAllGiasAssessment$ = createEffect(
    (cd: GiasAssessmentService = inject(GiasAssessmentService)) => {
      return this.actions$.pipe(

        ofType(GiasAssessmentActions.loadAllGiasAssessments),
        mergeMap(({ campaignId, parameters, attestationId, legalEntityIds }) =>
          cd.getGiasAssessment(campaignId, attestationId, legalEntityIds ?? null, parameters).pipe(
            map((giasAssessments) => {
              return GiasAssessmentActions.loadAllGiasAssessmentsSuccess({
                giasAssessments
              });
            }),
            catchError((error) => of(GiasAssessmentActions.loadAllGiasAssessmentsFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  createAndSignAssessment$ = createEffect(
    (
      cd: GiasAssessmentService = inject(GiasAssessmentService),
      messageService: MessageService = inject(MessageService)
    ) => {
      return this.actions$.pipe(
        ofType(GiasAssessmentActions.createAndSignAssessments),
        mergeMap(({ campaignId, legalEntityTrackingId, input }) =>
          cd.createAndSignAssessment(campaignId, legalEntityTrackingId, input).pipe(
            map((giasAssessments) =>
              GiasAssessmentActions.createAndSignAssessmentsSuccess({ giasAssessments })
            ),
            catchError((error) => {
              messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: error?.graphQLErrors?.[0]?.message || error?.message || 'An error occurred while signing the GIAS attestation',
                life: 10000
              });
              return of(GiasAssessmentActions.createAndSignAssessmentsFailure({ error }));
            })
          )
        )
      );
    },
    { functional: true }
  );

  exportGiasAssessment$ = createEffect(
    (giasAssessmentService: GiasAssessmentService = inject(GiasAssessmentService)) => {
      return this.actions$.pipe(
        ofType(GiasAssessmentActions.exportGiasAssessments),
        mergeMap(({ campaignId, timeZone, parameters, attestationId, legalEntityIds }) =>
          giasAssessmentService.exportGiasAssessment(campaignId, attestationId, legalEntityIds, timeZone, parameters).pipe(
            map((file) => {
              // Download diretto del file (se presente)
              if (file?.data && file.data.length > 0) {
                const f = file.data[0];
                const byteChars = atob(f!.content);
                const byteNumbers = new Array(byteChars.length);
                for (let i = 0; i < byteChars.length; i++) {
                  byteNumbers[i] = byteChars.charCodeAt(i);
                }
                const byteArray = new Uint8Array(byteNumbers);
                const blob = new Blob([byteArray], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
                const link = window.document.createElement("a");
                link.href = window.URL.createObjectURL(blob);
                link.download = f!.filename ?? "gias_assessment.xlsx";
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(link.href);
              }
              return GiasAssessmentActions.exportGiasAssessmentsSuccess({
                export: file
              });
            }),
            catchError((error) => of(GiasAssessmentActions.exportGiasAssessmentsFailure({ error })))
          )
        )
      )
    }
  );

  constructor(private actions$: Actions) { }
}
